import { gql } from 'apollo-boost'

const FRAGMENT_ANNOUNCEMENT = gql`
  fragment announcementFields on message {
    id
    data
    metadata: user_message_notification_metadata(where: {user_id: {_eq: $userId}}) {
      announcementId: message_id
      userId: user_id
      isRead: is_read
      isAcknowledged: is_acknowledged
    }
    senderUser: user {
      id
      email
    }
    recipients: message_groups {
      section: group {
        id
        name
        section {
          level {
            id
            name
          }
        }
      }
    }
    content {
      id
      title
      body
      attachments: content_assets {
        asset {
          id
          type
          description
          url
        }
      }
    }
    createdAt: created_at
  }
`

const FRAGMENT_ANNOUNCEMENT_METADATA = gql`
  fragment announcementMetaDataFields on user_message_notification_metadata {
    messageId: message_id
    userId: user_id
    isRead: is_read
    isAcknowledged: is_acknowledged
  }
`

export const QUERY_GENERAL_ANNOUNCEMENTS = gql`
  ${FRAGMENT_ANNOUNCEMENT}
  query GeneralAnnouncements ($userId: Int, $data: jsonb, $offset: Int, $limit: Int) {
    announcements: message(
      where: {
        type: {_eq: NOTIFICATION},
        status: {_eq: PUBLISHED},
        _not: {
          data: {_contains: $data}
        }
        _or: [
          {sender_user_id: {_eq: $userId}},
          {
            _not: {
              message_groups: {}
            }
          },
          {
            message_groups: {
              group: {
                user_groups: {
                  user_id: {_eq: $userId}
                }
              }
            }
          }
        ]
      },
      offset: $offset, limit: $limit,
      order_by: {created_at: desc}
    ) {
      ...announcementFields
    }
    announcements_aggregate: message_aggregate(
      where: {
        type: {_eq: NOTIFICATION},
        _not: {
          data: {_contains: $data}
        }
        _or: [
          {sender_user_id: {_eq: $userId}},
          {
            _not: {
              message_groups: {}
            }
          },
          {
            message_groups: {
              group: {
                user_groups: {
                  user_id: {_eq: $userId}
                }
              }
            }
          }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`

export const QUERY_PARENTS_ANNOUNCEMENTS = gql`
  ${FRAGMENT_ANNOUNCEMENT}
  query ParentsAnnouncements ($userId: Int, $data: jsonb, $offset: Int, $limit: Int) {
    announcements: message (
      where: {
        type: {_eq: NOTIFICATION},
        status: {_eq: PUBLISHED},
        data: {_contains: $data}
        _or: [
          {sender_user_id: {_eq: $userId}},
          {
            _not: {
              message_groups: {}
            }
          },
          {
            message_groups: {
              group: {
                user_groups: {
                  user_id: {_eq: $userId}
                }
              }
            }
          }
        ]
      },
      offset: $offset, limit: $limit,
      order_by: {created_at: desc}
    ) {
      ...announcementFields
    }
    announcements_aggregate: message_aggregate(
      where: {
        type: {_eq: NOTIFICATION},
        data: {_contains: $data}
        _or: [
          {sender_user_id: {_eq: $userId}},
          {
            _not: {
              message_groups: {}
            }
          },
          {
            message_groups: {
              group: {
                user_groups: {
                  user_id: {_eq: $userId}
                }
              }
            }
          }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`

export const QUERY_ANNOUNCEMENTS_BY_USER = gql`
  ${FRAGMENT_ANNOUNCEMENT}
  query AnnouncementsByUser ($userId: Int!, $offset: Int, $limit: Int) {
    announcements: message(
      where: {
        type: {_eq: NOTIFICATION},
        sender_user_id: {_eq: $userId}
      },
      offset: $offset, limit: $limit,
      order_by: {created_at: desc}
    ) {
      ...announcementFields
    }
    announcements_aggregate: message_aggregate(
      where: {
        type: {_eq: NOTIFICATION},
        sender_user_id: {_eq: $userId}
      }
    ) {
      aggregate {
        count
      }
    }
  }
`

export const QUERY_ANNOUNCEMENT = gql`
  ${FRAGMENT_ANNOUNCEMENT}
  query Announcement ($id: Int!, $userId: Int!) {
    announcement: message_by_pk(id: $id) {
      ...announcementFields
    }
  }
`

export const MUTATION_READ_ANNOUNCEMENT = gql`
  ${FRAGMENT_ANNOUNCEMENT_METADATA}
  mutation AcknowledgeAnnouncement($announcementId: Int!, $userId: Int!) {
    insert_user_message_notification_metadata_one(object: {
      message_id: $announcementId,
      user_id: $userId,
      is_read: true,
      is_acknowledged: false
    }, on_conflict: {
      constraint: user_message_notification_metadata_pkey,
      update_columns: [is_read]
    }) {
      ...announcementMetaDataFields
    }
  }
`

export const MUTATION_ACKNOWLEDGE_ANNOUNCEMENT = gql`
  ${FRAGMENT_ANNOUNCEMENT_METADATA}
  mutation AcknowledgeAnnouncement($announcementId: Int!, $userId: Int!) {
    insert_user_message_notification_metadata_one(object: {
      message_id: $announcementId,
      user_id: $userId,
      is_read: true,
      is_acknowledged: true
    }, on_conflict: {
      constraint: user_message_notification_metadata_pkey,
      update_columns: [is_acknowledged]
    }) {
      ...announcementMetaDataFields
    }
  }
`

export const MUTATION_PUBLISH_ANNOUNCEMENT = gql`
  ${FRAGMENT_ANNOUNCEMENT}
  mutation Announcement($userId: Int!, $data: jsonb, $title: String, $body: String, $recipients: [message_group_insert_input!]!, $attachments: [content_asset_insert_input!]!) {
    newAnnouncement: insert_message_one(object: {
      type: NOTIFICATION,
      status: PUBLISHED,
      sender_user_id: $userId,
      data: $data,
      content: {
        data: {
          title: $title,
          body: $body,
          content_assets: {
            data: $attachments
          }
        }
      },
      message_groups: {
        data: $recipients
      },
      user_message_notification_metadata: {
        data: {
          is_read: true,
          is_acknowledged: false,
          user_id: $userId
        }
      }
    }) {
      ...announcementFields
    }
  }
`

export const MUTATION_UPDATE_ANNOUNCEMENT = gql`
  ${FRAGMENT_ANNOUNCEMENT}
  mutation Announcement($id: Int!, $data: jsonb, $title: String, $body: String, $remainingAttachmentIds: [Int!]!, $attachments: [content_asset_insert_input!]!) {
    updatedContent: update_content(where: {messages: {id: {_eq: $id}}}, _set: {title: $title, body: $body}) {
      affected_rows
      returning {
        id
        title
        body
      }
    }
    delete_content_asset(where: {asset_id: {_nin: $remainingAttachmentIds}, content: {messages: {id: {_eq: $id}}}}) {
      affected_rows
    }
    insert_content_asset(objects: $attachments, on_conflict: {constraint: content_asset_pkey, update_columns: asset_id}) {
      affected_rows
    }
    updatedAnnouncement: update_message_by_pk(pk_columns: {id: $id}, _set: {data: $data}) {
      id
      data
    }
  }
`

export const SUBSCRIPTION_UNREAD_GENERAL_ANNOUNCEMENT_COUNT = gql`
  subscription UnreadGeneralAnnouncementsCount($userId: Int!, $data: jsonb) {
    announcements_aggregate: message_aggregate(
      where: {
        type: {_eq: NOTIFICATION},
        status: {_eq: PUBLISHED},
        _and: [
          {
            _not: {
              data: {_contains: $data}
            }
          },
          {
            _not: {
              user_message_notification_metadata: {
                user_id: {_eq: $userId}
              }
            }
          }
        ]
        _or: [
          {sender_user_id: {_eq: $userId}},
          {
            _not: {
              message_groups: {}
            }
          },
          {
            message_groups: {
              group: {
                user_groups: {
                  user_id: {_eq: $userId}
                }
              }
            }
          }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`

export const SUBSCRIPTION_UNREAD_PARENT_ANNOUNCEMENT_COUNT = gql`
  subscription UnreadParentAnnouncementsCount($userId: Int!, $data: jsonb) {
    announcements_aggregate: message_aggregate(
      where: {
        type: {_eq: NOTIFICATION},
        status: {_eq: PUBLISHED},
        _and: [
          {
            data: {_contains: $data}
          },
          {
            _not: {
              user_message_notification_metadata: {
                user_id: {_eq: $userId}
              }
            }
          }
        ]
        _or: [
          {sender_user_id: {_eq: $userId}},
          {
            _not: {
              message_groups: {}
            }
          },
          {
            message_groups: {
              group: {
                user_groups: {
                  user_id: {_eq: $userId}
                }
              }
            }
          }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`
